import { StatusBadge, CreatedByCell, TypeBadge, ActionDropdown, TitleCell } from './list-cell-renderers';
import { formatDateOrDash, parseUtcDate } from '../utils/cadence-data-mappers';

/**
 * Build MUI DataGrid column definitions for the Cadence Blueprints table.
 * Default visible: name, type, status, totalCadences, createdBy, typicalPerformance, actions.
 * Hidden by default (optional): automation, createdDate, avgDeliveryRate, avgBounceRate,
 *   avgUnsubscribeRate, avgOpenRate, avgClickRate, avgReplyRate, lastUpdated, tags.
 */
export const createCadenceShellsColumns = ({ currentUserId, actions, renamingState }) => [
  {
    field: 'name',
    headerName: 'Title',
    flex: 1,
    minWidth: 200,
    sortable: true,
    renderCell: ({ row }) => (
      <TitleCell
        row={{ ...row, subtitle: row.usageCount ? `Used ${row.usageCount} times` : undefined }}
        icon={row.type === 'Prospect Plan' ? 'person' : 'email'}
        onView={(id) => actions.onView('template', id)}
        renamingState={renamingState}
      />
    ),
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 0.6,
    minWidth: 140,
    sortable: true,
    renderCell: ({ row }) => <TypeBadge type={row.type} />,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 90,
    sortable: true,
    renderCell: ({ row }) => <StatusBadge status={row.status} />,
  },
  {
    field: 'totalCadences',
    headerName: 'Total Cadences',
    flex: 0.6,
    minWidth: 100,
    sortable: true,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => (value != null ? value.toLocaleString() : '\u2014'),
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueGetter: (value) => value?.name || '',
    renderCell: ({ row }) => <CreatedByCell createdBy={row.createdBy} currentUserId={currentUserId} />,
  },
  {
    field: 'typicalPerformance',
    headerName: 'Avg. Success',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    renderCell: ({ row }) =>
      row.typicalPerformance != null ? (
        <div className="cadences-v2-table--performance">
          <span className="cadences-v2-table--performance-value">{row.typicalPerformance}%</span>
          {row.performanceLabel && <span className="cadences-v2-table--performance-label">{row.performanceLabel}</span>}
        </div>
      ) : (
        '\u2014'
      ),
  },
  // Optional columns (hidden by default)
  {
    field: 'automation',
    headerName: 'Automation',
    flex: 0.7,
    minWidth: 140,
    sortable: true,
    valueFormatter: (value) => value || '\u2014',
  },
  {
    field: 'createdDate',
    headerName: 'Creation Date',
    flex: 0.7,
    minWidth: 110,
    sortable: true,
    type: 'date',
    valueGetter: parseUtcDate,
    valueFormatter: (value) => (value ? formatDateOrDash(value) : '\u2014'),
  },
  {
    field: 'avgDeliveryRate',
    headerName: 'Avg. Email Delivery Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgBounceRate',
    headerName: 'Avg. Email Bounce Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgUnsubscribeRate',
    headerName: 'Avg. Email Unsubscribe Rate',
    flex: 0.7,
    minWidth: 130,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgOpenRate',
    headerName: 'Avg. Email Open Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgClickRate',
    headerName: 'Avg. Email Click Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'avgReplyRate',
    headerName: 'Avg. Email Reply Rate',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueFormatter: (value) => (value != null ? `${value}%` : '\u2014'),
  },
  {
    field: 'lastUpdated',
    headerName: 'Last Updated',
    flex: 0.7,
    minWidth: 110,
    sortable: true,
    type: 'date',
    valueGetter: parseUtcDate,
    valueFormatter: (value) => (value ? formatDateOrDash(value) : '\u2014'),
  },
  {
    field: 'tags',
    headerName: 'Tags',
    flex: 0.8,
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) => {
      const tags = row._raw?.tags || [];
      if (!tags.length) return '\u2014';
      return (
        <div className="cadences-v2-table--tags">
          {tags.map((tag) => (
            <span key={tag} className="cadences-v2-table--tag-badge">
              {tag}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 80,
    sortable: false,
    filterable: false,
    hideable: false,
    type: 'actions',
    getApplyQuickFilterFn: () => null,
    renderCell: ({ row }) => (
      <ActionDropdown type="template" row={row} actions={actions} currentUserId={currentUserId} />
    ),
  },
];
